import { useRequest } from 'ahooks';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Drawer,
  message,
  Modal,
  Radio,
  Space,
  Spin,
  Statistic,
  Tag,
} from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ExamQuestionTypeEnum,
  MyExamItemDTO,
  SubmitExamResultDTO,
  examAttemptController,
} from '../../api';
import { useCompany } from '../../hooks/company';
import { optionLetter, questionTypeMapping, QuestionTypeTag } from './constants';

interface Props {
  exam: MyExamItemDTO;
  onClose: (finished: boolean) => void;
}

// 答题抽屉：start 幂等（断线恢复回填已答），倒计时按服务端时间校准，切题即保存，归零自动交卷
export default ({ exam, onClose }: Props) => {
  const { id: companyId } = useCompany();
  // paperQuestionId -> 已选字母数组
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [remainSeconds, setRemainSeconds] = useState<number>();
  const [result, setResult] = useState<SubmitExamResultDTO>();
  const submittingRef = useRef(false);
  // 保存答案的串行链：交卷前必须等它排空，否则评分可能早于最后一次答案写入
  const pendingSaveRef = useRef<Promise<unknown>>(Promise.resolve());

  const { data: attempt, loading } = useRequest(
    () => examAttemptController.start(companyId, exam.examId as I.ID),
    {
      onSuccess: (data) => {
        const saved: Record<string, string[]> = {};
        (data.savedAnswers || []).forEach((item) => {
          if (item.paperQuestionId && item.answer) {
            saved[String(item.paperQuestionId)] = item.answer.split(',');
          }
        });
        setAnswers(saved);
        if (data.deadlineAt && data.serverNow) {
          // 以服务端时间差校准，不信任本地时钟
          setRemainSeconds(
            Math.max(0, dayjs(data.deadlineAt).diff(dayjs(data.serverNow), 'second')),
          );
        }
      },
      onError: (e) => {
        message.error(e.message);
        onClose(false);
      },
    },
  );

  const questions = useMemo(() => attempt?.questions || [], [attempt]);
  const current = questions[currentIndex];

  const doSubmit = useCallback(
    async (auto: boolean) => {
      if (submittingRef.current || !attempt?.attemptId) {
        return;
      }
      submittingRef.current = true;
      try {
        // 等最后一次答案保存落库后再交卷
        await pendingSaveRef.current;
        const data = await examAttemptController.submit(
          companyId,
          attempt.attemptId,
        );
        setResult(data);
        if (auto) {
          message.warning('时间到，已自动交卷');
        }
      } catch (e: any) {
        message.error(e.message);
        submittingRef.current = false;
      }
    },
    [companyId, attempt],
  );

  // 倒计时；归零自动交卷
  useEffect(() => {
    if (remainSeconds === undefined || result) {
      return;
    }
    if (remainSeconds <= 0) {
      doSubmit(true);
      return;
    }
    const timer = setTimeout(() => setRemainSeconds(remainSeconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [remainSeconds, result, doSubmit]);

  // 答题中离开页面提示
  useEffect(() => {
    if (result) {
      return;
    }
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [result]);

  const saveAnswer = (paperQuestionId: I.ID, letters: string[]) => {
    setAnswers((prev) => ({ ...prev, [String(paperQuestionId)]: letters }));
    // 串行排队：连续操作/操作后立刻交卷时保证保存顺序与完成时机
    pendingSaveRef.current = pendingSaveRef.current
      .catch(() => undefined)
      .then(() =>
        examAttemptController.saveAnswer(companyId, attempt?.attemptId as I.ID, {
          paperQuestionId,
          answer: letters.join(','),
        }),
      )
      .catch((e: any) => {
        // 超时被服务端自动交卷等情况
        message.error(e.message);
        onClose(true);
      });
  };

  const answeredCount = useMemo(
    () => Object.values(answers).filter((item) => item.length > 0).length,
    [answers],
  );

  const confirmSubmit = () => {
    const unanswered = questions.length - answeredCount;
    Modal.confirm({
      title: '确认交卷？',
      content:
        unanswered > 0 ? `还有 ${unanswered} 题未作答，未作答不得分。` : '全部题目已作答。',
      okText: '交卷',
      onOk: () => doSubmit(false),
    });
  };

  // 交卷后的结果视图
  if (result) {
    return (
      <Drawer open title={exam.title} width="100%" onClose={() => onClose(true)} destroyOnClose>
        <Card style={{ maxWidth: 480, margin: '48px auto', textAlign: 'center' }}>
          <Statistic
            title={`${exam.title} · 第 ${attempt?.attemptNo} 次作答`}
            value={result.score}
            suffix={`/ ${result.totalScore} 分`}
            valueStyle={{ color: result.pass ? '#3f8600' : '#cf1322' }}
          />
          <div style={{ marginTop: 16 }}>
            {result.pass ? (
              <Tag color="green">通过（及格线 {result.passScore} 分）</Tag>
            ) : (
              <Tag color="red">未通过（及格线 {result.passScore} 分）</Tag>
            )}
          </div>
          <Button type="primary" style={{ marginTop: 24 }} onClick={() => onClose(true)}>
            返回我的考试
          </Button>
        </Card>
      </Drawer>
    );
  }

  return (
    <Drawer
      open
      title={
        <Space>
          {exam.title}
          {remainSeconds !== undefined && (
            <Tag color={remainSeconds < 60 ? 'red' : 'blue'}>
              剩余 {Math.floor(remainSeconds / 60)}:
              {String(remainSeconds % 60).padStart(2, '0')}
            </Tag>
          )}
        </Space>
      }
      width="100%"
      closable={false}
      destroyOnClose
      extra={
        <Space>
          <span>
            已答 {answeredCount} / {questions.length}
          </span>
          <Button type="primary" onClick={confirmSubmit}>
            交卷
          </Button>
          <Button onClick={() => onClose(true)}>暂时离开</Button>
        </Space>
      }
    >
      <Spin spinning={loading}>
        {!!attempt && !exam.durationMinutes && exam.endAt && (
          <Alert
            style={{ marginBottom: 16 }}
            type="info"
            message={`本场考试截止时间 ${dayjs(exam.endAt).format('YYYY-MM-DD HH:mm')}`}
          />
        )}
        <div style={{ display: 'flex', gap: 16 }}>
          <div style={{ flex: 1 }}>
            {current && (
              <Card
                title={
                  <Space>
                    <span>
                      第 {current.seq} 题（{current.score} 分）
                    </span>
                    <QuestionTypeTag type={current.type} />
                  </Space>
                }
              >
                <p style={{ fontSize: 16, marginBottom: 24 }}>{current.content}</p>
                {current.type === ExamQuestionTypeEnum.MULTIPLE ? (
                  <Checkbox.Group
                    style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
                    value={answers[String(current.id)] || []}
                    options={(current.options || []).map((item, index) => ({
                      label: `${optionLetter(index)}. ${item}`,
                      value: optionLetter(index),
                    }))}
                    onChange={(values) =>
                      saveAnswer(current.id as I.ID, values as string[])
                    }
                  />
                ) : (
                  <Radio.Group
                    style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
                    value={(answers[String(current.id)] || [])[0]}
                    options={(current.options || []).map((item, index) => ({
                      label: `${optionLetter(index)}. ${item}`,
                      value: optionLetter(index),
                    }))}
                    onChange={(event) =>
                      saveAnswer(current.id as I.ID, [event.target.value])
                    }
                  />
                )}
                <div style={{ marginTop: 32 }}>
                  <Space>
                    <Button
                      disabled={currentIndex === 0}
                      onClick={() => setCurrentIndex(currentIndex - 1)}
                    >
                      上一题
                    </Button>
                    <Button
                      type="primary"
                      disabled={currentIndex >= questions.length - 1}
                      onClick={() => setCurrentIndex(currentIndex + 1)}
                    >
                      下一题
                    </Button>
                  </Space>
                </div>
              </Card>
            )}
          </div>
          <Card title="答题卡" style={{ width: 280, alignSelf: 'flex-start' }}>
            {/* 按题型分组展示题号；组的先后跟随卷面题序（哪种题型先出现哪组在前），组内按题号升序 */}
            {Array.from(
              questions.reduce((groups, item, index) => {
                const type = item.type as ExamQuestionTypeEnum;
                const group = groups.get(type) || [];
                group.push({ item, index });
                groups.set(type, group);
                return groups;
              }, new Map<ExamQuestionTypeEnum, { item: (typeof questions)[number]; index: number }[]>()),
            ).map(([groupType, group]) => {
              return (
                <div key={groupType} style={{ marginBottom: 12 }}>
                  <div style={{ color: '#888', marginBottom: 6 }}>
                    {questionTypeMapping[groupType]}（{group.length} 题）
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {group.map(({ item, index }) => {
                      const answered = !!answers[String(item.id)]?.length;
                      return (
                        <Button
                          key={String(item.id)}
                          size="small"
                          type={index === currentIndex ? 'primary' : 'default'}
                          style={
                            answered && index !== currentIndex
                              ? { background: '#f6ffed', borderColor: '#b7eb8f' }
                              : undefined
                          }
                          onClick={() => setCurrentIndex(index)}
                        >
                          {item.seq}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </Card>
        </div>
      </Spin>
    </Drawer>
  );
};
